import type { RichTextStrings } from "../components/editor/strings";
import type { ImageUploadStrings } from "../fields/ImageUploadField";
import type { TagPickerStrings } from "../fields/TagChipsField";
import type { StreamKey } from "@one-ai/shared/cms/streams";
import { cmsLocales, defaultCmsLocale, type CmsLocale } from "./locales";

/**
 * Every editor-facing string that belongs to *our* collections, rather than to
 * FireCMS's chrome.
 *
 * **Why this exists as a separate system from `locales.ts`.** FireCMS's i18n
 * translates its own UI and nothing else: collection and property `name` /
 * `description` are typed `string` in `EntityCollection`, and property builders
 * are not handed a locale, so there is no hook for FireCMS to localise them
 * through. These strings are ours, so we carry them ourselves — the same shape
 * the marketing apps use for `lib/i18n/dictionaries/*`.
 *
 * **Interpolation is a function, not a `{{placeholder}}`.** Our strings never
 * pass through i18next, so a template function is both simpler and type-checked:
 * forget an argument and the build fails, where a missing placeholder would
 * print literally.
 *
 * Language names (`繁體中文`, `日本語`) are deliberately absent. A language is
 * always named in itself, so those sub-labels are the same in every locale —
 * see `localeLabels` in `locales.ts` for the same rule.
 */
interface Field {
    name: string;
    description?: string;
}

/** A `StatusSwitchField`'s two state labels. */
interface Toggle {
    onLabel: string;
    offLabel: string;
}

interface CollectionMeta {
    name: string;
    singularName: string;
    description: string;
}

/**
 * The only strings that differ between streams: what a collection is called in
 * the drawer, and the URL fragment inside its slug error.
 *
 * Everything else an editor reads — every field label, every toggle, both
 * toolbar buttons — is identical across streams, because one article is one
 * article. That is why `article` and `tag` below are stated once rather than
 * per stream: duplicating them would let two streams' labels drift apart with
 * nothing to catch it.
 */
interface StreamMeta {
    collection: CollectionMeta;
    /** Thrown from `onPreSave` when a custom document id is not kebab-case. */
    invalidSlug: (slug: string) => string;
}

export interface CollectionStrings {
    /** Identical across streams — one article is one article. */
    article: {
        /**
         * Thrown from `onPreSave` when the article carries a retired tag —
         * reachable by searching the picker, where a text search suspends the
         * `forceFilter`, or by retiring a tag while the form is open.
         */
        retiredTags: (names: string[]) => string;
        /**
         * The "New article" toolbar button — the only way to create an article,
         * since its number is assigned rather than typed. See
         * `components/NewArticleAction.tsx`.
         */
        newArticle: {
            button: string;
            /**
             * Shown when the record could not be written at all, so nothing was
             * created and the number was not consumed.
             */
            failed: string;
        };
        fields: {
            date: Field;
            updatedAt: Field;
            slug: Field;
            published: Field & Toggle;
            tags: Field;
            /** News stream only — the blog editor never shows it. */
            product: Field;
            img: Field;
            title: Field;
            description: Field;
            body: Field;
        };
    };
    /** Identical across streams. */
    tag: {
        /**
         * The "New tag" toolbar button — the only way to create a tag, since
         * its number is assigned rather than typed. See
         * `components/NewTagAction.tsx`.
         */
        newTag: {
            button: string;
            /**
             * Shown when the record could not be written at all, so nothing was
             * created and the number was not consumed.
             */
            failed: string;
        };
        fields: {
            date: Field;
            label: Field;
            /**
             * The listing's filter-bar curation flag. Gates the pills in
             * `TagFilterBar` and nothing else — a non-category tag keeps its
             * page and its chips.
             */
            category: Field & Toggle;
            retired: Field & Toggle;
            order: Field;
        };
    };
    /**
     * The per-stream half, keyed by `StreamKey`. A new stream in
     * `packages/shared/cms/streams.ts` makes every locale file stop compiling
     * until it has a name for both of that stream's collections — which is the
     * point.
     */
    streams: Record<StreamKey, { article: StreamMeta; tag: StreamMeta }>;
    announcements: {
        collection: CollectionMeta;
        fields: { date: Field; type: Field; title: Field; url: Field };
    };
    cases: {
        collection: CollectionMeta;
        fields: {
            date: Field;
            title: Field;
            description: Field;
            img: Field;
            url: Field;
        };
    };
    /** The `ImageUploadField`'s controls and failure messages. */
    upload: ImageUploadStrings;
    /**
     * The `TagChipsField`'s controls — the chip row on a news article.
     *
     * Field-level rather than under `news.fields.tags`, alongside `upload` and
     * `richText`, because these are a *widget's* strings: they reach the
     * component as `customProps`, not as a property `name`.
     */
    tagPicker: TagPickerStrings;
    /**
     * The body editor's toolbar, its URL prompts and its media controls.
     *
     * Carries its own copy of `upload` rather than reusing the one above: the
     * hero image and a body image fail in the same ways but are not the same
     * control, and the hero's `preview` alt text ("Hero image preview") would
     * be wrong on a body image.
     */
    richText: RichTextStrings & { placeholder: string };
    /** The deploy-hook UI: toolbar buttons and save-time snackbars. */
    rebuild: {
        button: (environment: string) => string;
        tooltip: (environment: string) => string;
        triggered: (environments: string) => string;
        allFailed: (environments: string) => string;
        partial: (ok: string, failed: string) => string;
    };
}

/**
 * Resolved bundles, filled in by `register` below so each locale file stays a
 * plain object and this module stays the only place that knows the fallback.
 */
const bundles = {} as Record<CmsLocale, CollectionStrings>;

export function registerCollectionStrings(
    locale: CmsLocale,
    strings: CollectionStrings,
): void {
    bundles[locale] = strings;
}

/**
 * Strings for a locale, falling back to the default.
 *
 * Unlike FireCMS's per-key fallback, this falls back per *bundle* — the
 * `CollectionStrings` type is total, so a registered locale is complete by
 * construction and there is no partial case to handle.
 */
export function collectionStringsFor(locale: string): CollectionStrings {
    return bundles[locale as CmsLocale] ?? bundles[defaultCmsLocale];
}

/**
 * The active locale, for code that runs outside React and so cannot use
 * `useTranslation()` — `onSaveRebuild`, which FireCMS calls as a plain
 * callback.
 *
 * Reads the key `FireCMSi18nProvider` writes on every `languageChanged`, so it
 * always agrees with what the editor sees. Falls back to the default when
 * nothing is stored, which is also the provider's own behaviour.
 */
export function currentCmsLocale(): CmsLocale {
    if (typeof window === "undefined") return defaultCmsLocale;
    const stored = window.localStorage.getItem("firecms_locale");
    return (cmsLocales as readonly string[]).includes(stored ?? "")
        ? (stored as CmsLocale)
        : defaultCmsLocale;
}
